import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { NewsletterOptIn } from "@/components/NewsletterOptIn";
import { trackEvent } from "@/lib/analytics";
import Seo from "@/components/Seo";
import ScoutBox from "@/components/ScoutBox";

const SCENARIOS = [
  "Playing Time Concerns",
  "Sideline Behavior",
  "Attendance & Commitment",
  "Position Preference",
  "Post-Game Debrief",
];

export default function ParentScript() {
  const [form, setForm] = useState({ email: "", name: "", scenario: SCENARIOS[0], child_age: "", details: "", team_mom: false, newsletter_opt_in: false });
  const [busy, setBusy] = useState(false);
  const [script, setScript] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Add an email so we can save this."); return; }
    setBusy(true);
    setScript(null);
    try {
      const { data } = await api.post("/parent-script", {
        email: form.email, name: form.name || null,
        scenario: form.scenario, child_age: form.child_age || null,
        details: form.details || null, team_mom: form.team_mom, newsletter_opt_in: form.newsletter_opt_in,
      });
      if (form.newsletter_opt_in) trackEvent("newsletter_signup", { source: "parent_script" });
      setScript(data.script);
      toast.success("Scout wrote your script.");
    } catch {
      toast.error("Couldn't build that. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-12" data-testid="parent-script-page">
      <Seo title="Parent Conversation Script Builder" description="Script the hard talk with your kid's coach before it happens — free tool from Voluntold." path="/tools/parent-script" />
      <span className="kicker">Free Tool</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-3xl lg:text-5xl text-[#1E1E1E] leading-[1.05]">Parent Conversation Script Builder</h1>
      <p className="mt-3 text-[#6E6E6E]">Pick the conversation you're dreading. Scout hands you the words — what to say, what to skip, and a follow-up email.</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div>
          <label className="kicker">Scenario</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {SCENARIOS.map((s) => (
              <button type="button" key={s} onClick={() => setForm({ ...form, scenario: s })} data-testid={`ps-scenario-${s.split(" ")[0].toLowerCase()}`}
                className={`px-4 py-2 border-2 font-head uppercase tracking-wide text-xs transition-colors ${form.scenario === s ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] bg-[#E7E3D9] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="kicker">Child's age (optional)</label>
            <input value={form.child_age} onChange={(e) => setForm({ ...form, child_age: e.target.value })}
              data-testid="ps-age-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="e.g. 10" />
          </div>
          <div>
            <label className="kicker">Name (optional)</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="ps-name-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" />
          </div>
        </div>
        <div>
          <label className="kicker">Anything specific? (optional)</label>
          <textarea rows="3" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
            data-testid="ps-details-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="Context helps Scout make it specific." />
        </div>
        <div>
          <label className="kicker">Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="ps-email-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="you@email.com" />
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.team_mom} onChange={(e) => setForm({ ...form, team_mom: e.target.checked })}
            data-testid="ps-teammom-checkbox" className="w-5 h-5 accent-[#2D2D2D]" />
          <span className="text-[#1E1E1E] text-[15px]">I also handle team logistics (team mom / team parent role).</span>
        </label>
        <NewsletterOptIn checked={form.newsletter_opt_in} onChange={(v) => setForm({ ...form, newsletter_opt_in: v })} testId="ps-newsletter-optin" />
        <button type="submit" disabled={busy} data-testid="ps-submit-btn"
          className="w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
          {busy ? "Scout is writing…" : "Build my script"}
        </button>
      </form>

      {script && (
        <div className="mt-12 space-y-6 rise" data-testid="ps-result">
          <div className="border-2 border-[#1E1E1E] bg-[#E7E3D9] p-6">
            <span className="kicker">Objective</span>
            <p className="mt-2 text-[#1E1E1E] text-[15px] leading-relaxed">{script.objective}</p>
          </div>
          <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-6">
            <h3 className="font-head uppercase text-lg text-[#2D2D2D]">What to say</h3>
            <ul className="mt-3 space-y-3">
              {script.what_to_say.map((s, i) => (
                <li key={i} className="flex gap-3 text-[#1E1E1E] text-[15px]"><span className="text-[#E8A33D] font-head">›</span> "{s}"</li>
              ))}
            </ul>
          </div>
          <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-6">
            <h3 className="font-head uppercase text-lg text-[#2D2D2D]">What not to say</h3>
            <ul className="mt-3 space-y-3">
              {script.what_not_to_say.map((s, i) => (
                <li key={i} className="flex gap-3 text-[#6E6E6E] text-[15px]"><span className="text-[#B44] font-head">✕</span> {s}</li>
              ))}
            </ul>
          </div>
          <ScoutBox title="Follow-up email template" testId="ps-followup">
            <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed">{script.followup_email}</pre>
          </ScoutBox>
        </div>
      )}
    </div>
  );
}
